// Loopback latency — the pure half of calibrating the record offset.
//
// The engine plays a train of clicks at known times while the mic records;
// what comes back is the same train, late by the round trip (output buffer
// + speaker → mic + input buffer + the recorder's own start). Finding that
// lag is a matched-filter search: slide the expected click times across the
// recording's envelope and keep the lag where the most energy lines up.
// Pure over sample arrays, so a synthetic recording proves it.

export interface LatencyEstimate {
  /** The lag, in milliseconds. */
  offsetMs: number;
  /** Peak score over the mean score across lags: ~1 is noise, ≥3 is a hit. */
  confidence: number;
}

/** Peak-hold envelope with a window of `windowSamples`. */
function envelope(samples: Float32Array, windowSamples: number): Float32Array {
  const out = new Float32Array(samples.length);
  let peak = 0;
  let peakAt = -1;
  for (let i = 0; i < samples.length; i++) {
    const v = Math.abs(samples[i] ?? 0);
    if (v >= peak || i - peakAt > windowSamples) {
      // Re-scan the window when the held peak has fallen out of it.
      peak = 0;
      const from = Math.max(0, i - windowSamples);
      for (let j = from; j <= i; j++) {
        const w = Math.abs(samples[j] ?? 0);
        if (w >= peak) {
          peak = w;
          peakAt = j;
        }
      }
    }
    out[i] = peak;
  }
  return out;
}

/**
 * The lag at which `clickTimesSec` best explains the recording. Searches
 * 0..`maxLagMs` at 1 ms steps, then refines to the sample at the best
 * millisecond. Null when the recording is silent or shorter than the train.
 */
export function estimateLatency(
  samples: Float32Array,
  sampleRate: number,
  clickTimesSec: readonly number[],
  maxLagMs = 500,
): LatencyEstimate | null {
  if (samples.length === 0 || clickTimesSec.length === 0 || !(sampleRate > 0)) return null;
  const clicks = clickTimesSec.map((t) => Math.round(t * sampleRate));
  const lastClick = Math.max(...clicks);
  if (lastClick >= samples.length) return null;
  let peak = 0;
  for (let i = 0; i < samples.length; i++) peak = Math.max(peak, Math.abs(samples[i] ?? 0));
  if (peak < 1e-4) return null;

  const env = envelope(samples, Math.round(sampleRate * 0.002));
  const stepSamples = Math.max(1, Math.round(sampleRate / 1000));
  const maxLag = Math.min(Math.round((maxLagMs / 1000) * sampleRate), samples.length - lastClick - 1);
  if (maxLag <= 0) return null;

  const score = (lag: number): number => {
    let s = 0;
    for (const c of clicks) s += env[c + lag] ?? 0;
    return s;
  };
  let best = 0;
  let bestScore = -1;
  let total = 0;
  let count = 0;
  for (let lag = 0; lag <= maxLag; lag += stepSamples) {
    const s = score(lag);
    total += s;
    count++;
    if (s > bestScore) {
      bestScore = s;
      best = lag;
    }
  }
  // Refine within the winning millisecond.
  for (let lag = Math.max(0, best - stepSamples); lag <= Math.min(maxLag, best + stepSamples); lag++) {
    const s = score(lag);
    if (s > bestScore) {
      bestScore = s;
      best = lag;
    }
  }
  const mean = count > 0 ? total / count : 0;
  const confidence = mean > 0 ? bestScore / mean : 0;
  return { offsetMs: Math.round((best / sampleRate) * 1000 * 10) / 10, confidence };
}

/** The click train the calibration plays: eight clicks, a quarter second apart,
 * starting half a second in so the recorder is surely rolling. */
export const CALIBRATION_CLICKS: readonly number[] = Array.from({ length: 8 }, (_, i) => 0.5 + i * 0.25);
/** How long the recording runs: past the last click plus the longest lag searched. */
export const CALIBRATION_SECONDS = CALIBRATION_CLICKS[CALIBRATION_CLICKS.length - 1]! + 0.8;
